import React, { useEffect, useState } from 'react';
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
	LineChart,
	Line,
	LabelList,
	Area,
	AreaChart,
} from 'recharts';
import moment from 'moment';
import { analyticsAPI } from '../../services/constants';
import { capitalizeAndRemoveHyphen, formatHyphenatedText, mapTimeRangeToApiFilterAlt3, request } from '../../services/utilities';
import TableLoading from '../../components/TableLoading';
import SlidingPane from '../../components/SlidingPane';
import { Statistic } from 'antd';
import { TimeRangeDropdownAlt3 } from '../../components/TimeRangeDropdownAlt3';
import { CustomTooltipAlt2 } from '../Doctor/Overview';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ff6e54', '#ffc0cb', '#faad14'];

const IVFOverview = () => {
	const [timeRanges, setTimeRanges] = useState({
		overviewMetrics: 'past30Days',
		treatmentPlan: 'past30Days',
		prognosis: 'past30Days',
		indication: 'past30Days',
		enrollmentTrend: 'past30Days',
		enrollmentClosed: 'past30Days',
		amountItem: 'past30Days',
		orderAmount: 'past30Days',
		orderRank: 'past30Days',
		orderTrends: 'past30Days',
		orderCount: 'past30Days',
		orderTime: 'past30Days',
		orderTrendsAmount: 'past30Days',
		orderCustomerType: 'all-time',
		orderReadyBy: 'past30Days',
		orderCategory: 'past30Days',
		orderDescription: 'past30Days',
		staffRank: 'past30Days',
	});

	const [loadingStates, setLoadingStates] = useState({
		overviewMetrics: false,
		treatmentPlan: false,
		prognosis: false,
		indication: false,
		enrollmentTrend: false,
		enrollmentClosed: false,
		staffRank: false,
		amountItem: false,
		orderAmount: false,
		orderRank: false,
		orderTrends: false,
		orderCount: false,
		orderTime: false,
		orderTrendsAmount: false,
		orderCustomerType: false,
		orderReadyBy: false,
		orderCategory: false,
		orderDescription: false,
	});

	const [metrics, setMetrics] = useState();
	const [treatmentPlan, setTreatmentPlan] = useState(null);
	const [prognosis, setPrognosis] = useState(null);
	const [indication, setIndication] = useState(null);
	const [enrollmentTrend, setEnrollmentTrend] = useState(null);
	const [enrollmentClosed, setEnrollmentClosed] = useState(null);
	const [staffRank, setStaffRank] = useState([]);
	const [modalAlt, setModalAlt] = useState(false);

	// Api calls
	const fetchMetrics = async range => {
		setLoadingStates(prev => ({ ...prev, overviewMetrics: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-enrollment-metrics?dateFilter=${dateFilter}`, 'GET', true);
			const apiResponseTop = res?.result;
			setMetrics(apiResponseTop);
		} catch (error) {
			console.error('Failed to fetch data:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, overviewMetrics: false }));
		}
	};

	// Fetch Treatment Plan
	const fetchTreatmentPlan = async range => {
		setLoadingStates(prev => ({ ...prev, treatmentPlan: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-treatment-plan?/treatment-plan?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				name: x?.treatmentPlan,
				count: Number(x?.count),
			}));
			setTreatmentPlan(apiResponse);
		} catch (error) {
			console.error('Failed to fetch treatmentPlan:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, treatmentPlan: false }));
		}
	};

	// Fetch Prognosis
	const fetchPrognosis = async range => {
		setLoadingStates(prev => ({ ...prev, prognosis: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-prognosis?/prognosis?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				name: x?.prognosis,
				count: parseInt(x?.count, 10),
			}));
			setPrognosis(apiResponse);
		} catch (error) {
			console.error('Failed to fetch prognosis:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, prognosis: false }));
		}
	};

	// Fetch Indication
	const fetchIndication = async range => {
		setLoadingStates(prev => ({ ...prev, indication: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-indication?/indication?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				name: x?.indication,
				count: parseInt(x?.count, 10),
			}));
			setIndication(apiResponse);
		} catch (error) {
			console.error('Failed to fetch indication:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, indication: false }));
		}
	};

	// Fetch Enrollment Trend
	const fetchEnrollmentTrend = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentTrend: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-enrollment-trend?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				date:
					x?.date ||
					(dateFilter == 'past-three-years' && x?.month && moment(x?.month).format('MMMM, YY')) ||
					(x?.month && moment(x?.month).format('MMMM')) ||
					x?.day ||
					x?.week ||
					x?.year ||
					x?.hour,
				count: parseInt(x?.count, 10),
			}));
			setEnrollmentTrend(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentTrend:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentTrend: false }));
		}
	};

	// Fetch Enrollment Closed
	const fetchEnrollmentClosed = async range => {
		setLoadingStates(prev => ({ ...prev, enrollmentClosed: true }));
		const dateFilter = mapTimeRangeToApiFilterAlt3(range);
		try {
			const res = await request(`analytics-v2/ivf-enrollment-closed-by-staff?dateFilter=${dateFilter}`, 'GET', true);

			const apiResponse = res?.result?.map(x => ({
				name: x?.staff,
				count: parseInt(x?.count, 10),
			}));
			setEnrollmentClosed(apiResponse);
		} catch (error) {
			console.error('Failed to fetch enrollmentClosed:', error);
		} finally {
			setLoadingStates(prev => ({ ...prev, enrollmentClosed: false }));
		}
	};

	const fetchStaffRank = async range => {
		setLoadingStates(prev => ({ ...prev, staffRank: true }));
		try {
			const dateFilter = mapTimeRangeToApiFilterAlt3(range);
			const url = `analytics-v2/get-staff-request-count-by-role?roles=ivf&dateFilter=${dateFilter}`;
			const res = await request(url, 'GET', true);
			const apiResponse = res?.map(x => ({
				...x,
				name: x?.username || 'unknown',
				role: x?.role,
			}));
			console.log(apiResponse, 'staff rank...');
			setStaffRank(apiResponse || []);
		} catch (error) {
			console.error('Failed to fetch staff rank:', error);
			setStaffRank([]);
		} finally {
			setLoadingStates(prev => ({ ...prev, staffRank: false }));
		}
	};

	//initial data load
	useEffect(() => {
		fetchMetrics(timeRanges.overviewMetrics);
		fetchTreatmentPlan(timeRanges.treatmentPlan);
		fetchPrognosis(timeRanges.prognosis);
		fetchIndication(timeRanges.indication);
		fetchEnrollmentTrend(timeRanges.enrollmentTrend);
		fetchEnrollmentClosed(timeRanges.enrollmentClosed);
		fetchStaffRank(timeRanges.staffRank);
	}, []);

	//handle time range changes
	const handleTimeRangeChange = (range, chartId) => {
		setTimeRanges(prev => ({ ...prev, [chartId]: range }));

		switch (chartId) {
			case 'overviewMetrics':
				fetchMetrics(range);
				break;
			case 'treatmentPlan':
				fetchTreatmentPlan(range);
				break;
			case 'prognosis':
				fetchPrognosis(range);
				break;
			case 'indication':
				fetchIndication(range);
				break;
			case 'enrollmentTrend':
				fetchEnrollmentTrend(range);
				break;
			case 'enrollmentClosed':
				fetchEnrollmentClosed(range);
				break;
			case 'staffRank':
				fetchStaffRank(range);
				break;
			default:
				break;
		}
	};

	const renderCustomLabelAlt = ({ x, y, value, fill }) => {
		return (
			<text x={x} y={y} dy={-6} textAnchor="middle" fill={fill || '#333'} fontSize={12}>
				{value?.toLocaleString()}
			</text>
		);
	};

	const CustomTooltipAlt = ({ active, payload }) => {
		if (active && payload && payload?.length) {
			const data = payload[0]?.payload;
			return (
				<div style={{ background: '#fff', border: '1px solid #ccc', padding: '8px' }}>
					<p className="text-capitalize">{data?.name}</p>
					<p>
						<strong>Count:</strong> {data?.count?.toLocaleString()}
					</p>
				</div>
			);
		}
		return null;
	};

	const MetricsConfig = [
		{ key: 'totalEnrollments', label: 'Total Enrollments', color: '#4F46E5' },
		{ key: 'ENROLLED', label: 'Active', color: '#10B981' },
		{ key: 'CLOSED', label: 'Closed', color: '#6B7280' },
		{ key: 'discontinue', label: 'discontinue', color: '#F59E0B' },
		{ key: 'CANCELLED', label: 'Cancelled', color: '#EF4444' },
	];

	const MetricsCard = ({ loading, value, label, total, color }) => {
		const percentage = value != null && total > 0 ? ((value / total) * 100).toFixed(1) : 0;

		return (
			<div className="col-sm-4">
				<a className="element-box el-tablo" href="#">
					<Statistic
						title={label}
						value={loading ? <TableLoading /> : value != null ? Number(value).toLocaleString() : '0'}
						valueStyle={{ fontWeight: 'bold', color }}
					/>
					{label !== 'Total Enrollments' ? (
						<small className="text-muted">
							<strong style={{ color }}>{percentage}%</strong> of Total Enrollment for{' '}
						</small>
					) : (
						<small className="text-muted">Total Enrollment for</small>
					)}{' '}
					<small className="text-muted">
						<strong>{formatHyphenatedText(mapTimeRangeToApiFilterAlt3(timeRanges.overviewMetrics))}</strong>
					</small>
				</a>
			</div>
		);
	};

	return (
		<div className="content-">
			<div className="content-bo">
				<div className="row">
					{/* Overview Metrics */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">IVF Overview</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.overviewMetrics} onChange={handleTimeRangeChange} chartId="overviewMetrics" />
							</div>
							<p className="chart-description mb-3 p-2 card">Summary metrics showing overall IVF activity.</p>
							<div className="element-bo">
								<div className="ro">
									{loadingStates.overviewMetrics ? (
										<div className="element-box">
											<TableLoading />
										</div>
									) : (
										<div className="row">
											{MetricsConfig.map(({ key, label, color }) => {
												const value = metrics ? metrics[key] : 0;
												return (
													<MetricsCard
														key={key}
														label={label}
														color={color}
														value={value}
														total={metrics?.totalEnrollments}
														loading={loadingStates?.[key]}
													/>
												);
											})}
										</div>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* IVF Treatment Plan */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">IVF Treatment Plan</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.treatmentPlan} onChange={handleTimeRangeChange} chartId="treatmentPlan" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart shows the distribution of patients across different IVF treatment plans within the selected time frame. This
								helps you understand which treatment plans are most commonly adopted.
							</p>
							<div className="element-box">
								{loadingStates?.treatmentPlan ? (
									<TableLoading />
								) : treatmentPlan?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<PieChart>
												<Pie
													data={treatmentPlan}
													dataKey="count"
													nameKey="name"
													cx="50%"
													cy="50%"
													outerRadius={120}
													label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
												>
													{treatmentPlan.map((entry, index) => (
														<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
													))}
												</Pie>
												<Tooltip content={<CustomTooltipAlt />} />
												<Legend wrapperStyle={{ marginBottom: '-10px' }} formatter={value => capitalizeAndRemoveHyphen(value)} />
											</PieChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/*Staff Rank */}
					<div className="col-12 mt-4">
						<div className="element-wrapper">
							<h6 className="element-header">Staff Request Ranking</h6>

							<div className="d-flex justify-content-end w-100">
								<div>
									<div
										style={{ width: '100px', cursor: 'pointer', textAlign: 'center' }}
										onClick={() => setModalAlt(true)}
										className="form-control form-control-sm text-white bg-primary rounded"
									>
										View All
									</div>
								</div>
								&nbsp;
								<TimeRangeDropdownAlt3 chartId="staffRank" onChange={handleTimeRangeChange} value={timeRanges?.staffRank} />
							</div>

							<p className="chart-description card p-2" style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
								The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
								roles, helping identify which roles generate the highest number of requests based on the selected filter.
							</p>
							<div className="row">
								<div className="col-md-12 mb-4">
									<div className="card">
										{loadingStates.staffRank ? (
											<TableLoading />
										) : staffRank ? (
											<div style={{ width: '100%', height: 400 }}>
												<ResponsiveContainer>
													<LineChart
														data={staffRank.slice(0, 30)}
														layout="horizontal"
														margin={{ top: 20, right: 20, left: 20, bottom: 55 }}
													>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis
															dataKey={'name'}
															angle={-45}
															textAnchor="end"
															fontSize={12}
															interval={0}
															tickFormatter={value => {
																const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																const capitalized = truncated
																	.toLowerCase()
																	.split(' ')
																	.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																	.join(' ');
																return capitalized;
															}}
														/>
														<YAxis domain={[0, 'auto']} />
														<Tooltip content={<CustomTooltipAlt2 />} />

														<Line
															type="monotone"
															dataKey="total"
															stroke="#22BB33"
															strokeWidth={2}
															dot={{ r: 2 }}
															activeDot={{ r: 4 }}
														></Line>
													</LineChart>
												</ResponsiveContainer>
											</div>
										) : (
											<div className="text-center fs-bold">No Record Found!</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<SlidingPane isOpen={modalAlt}>
							<div className=" content-box">
								<div className=" d-flex justify-content-end w-100">
									<button className="btn btn-danger float-end" onClick={() => setModalAlt(false)}>
										&times;
									</button>
								</div>

								<div className=" d-flex justify-content-end mt-5 t-5 align-items-center">
									<TimeRangeDropdownAlt3 value={timeRanges.staffRank} onChange={handleTimeRangeChange} chartId="staffRank" />
								</div>
								<div className="row  w-100 " style={{ paddingLeft: '50px', paddingRight: '50px' }}>
									<div className="col-sm-12">
										<div className="element-wrapper">
											<h6 className="element-header">Staff Request Ranking</h6>
										</div>
									</div>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<p className="chart-description card p-2">
										The Staff Request Ranking by Role chart highlights the distribution and frequency of staff requests across different job
										roles, helping identify which roles generate the highest number of requests based on the selected filter.
									</p>
								</div>

								<div className=" " style={{ marginLeft: '50px', marginRight: '50px' }}>
									<div className=" card w-100 ">
										<div className="row">
											<div className="col-md-12 mb-4">
												<div className="card h-100">
													<div className="card-body">
														{loadingStates.staffRank ? (
															<TableLoading />
														) : staffRank ? (
															<div style={{ width: '100%', height: 400 }}>
																<ResponsiveContainer>
																	<LineChart data={staffRank} layout="horizontal" margin={{ top: 20, right: 20, left: 20, bottom: 55 }}>
																		<CartesianGrid strokeDasharray="3 3" />
																		<XAxis
																			dataKey={'name'}
																			angle={-45}
																			textAnchor="end"
																			fontSize={12}
																			interval={2}
																			tickFormatter={value => {
																				const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
																				const capitalized = truncated
																					.toLowerCase()
																					.split(' ')
																					.map(word => word.charAt(0).toUpperCase() + word.slice(1))
																					.join(' ');
																				return capitalized;
																			}}
																		/>
																		<YAxis domain={[0, 'auto']} />
																		<Tooltip content={<CustomTooltipAlt2 />} />

																		<Line
																			type="monotone"
																			dataKey="total"
																			stroke="#22BB33"
																			strokeWidth={2}
																			dot={{ r: 2 }}
																			activeDot={{ r: 4 }}
																		></Line>
																	</LineChart>
																</ResponsiveContainer>
															</div>
														) : (
															<div className="text-center fs-bold">No Record Found!</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</SlidingPane>
					</div>

					{/* Prognosis */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">Prognosis</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.prognosis} onChange={handleTimeRangeChange} chartId="prognosis" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart displays the distribution of patients by prognosis within the selected time frame. This helps you evaluate
								outcomes and understand the overall success expectations for different patient groups.
							</p>
							<div className="element-box">
								{loadingStates?.prognosis ? (
									<TableLoading />
								) : prognosis?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={prognosis} margin={{ top: 20, right: 40, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />

												<Tooltip content={CustomTooltipAlt} />
												<Bar dataKey="count" fill="#8884d8">
													<LabelList dataKey="count" position="top" />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* IVF Indication */}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">IVF Indication</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.indication} onChange={handleTimeRangeChange} chartId="indication" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart shows the distribution of medical indications for IVF treatments within the selected time frame. This helps
								identify the most common reasons patients undergo IVF and provides insight into treatment demand patterns.
							</p>
							<div className="element-box">
								{loadingStates?.indication ? (
									<TableLoading />
								) : indication?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={indication} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.indication === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* IVF Enrollment Trend*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header"> IVF Enrollment Trend</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.enrollmentTrend} onChange={handleTimeRangeChange} chartId="enrollmentTrend" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								This chart tracks the trend of IVF patient enrollments over the selected time frame. This helps monitor growth, spot
								seasonal patterns, and evaluate the effectiveness of outreach or treatment programs.
							</p>

							<div className="element-box">
								{loadingStates.enrollmentTrend ? (
									<TableLoading />
								) : enrollmentTrend?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<AreaChart data={enrollmentTrend} margin={{ top: 20, right: 20, left: 10, bottom: 50 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'date'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={timeRanges.enrollmentTrend === 'pastQuarter' ? 2 : 0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />
												<Tooltip
													formatter={value => Number(value).toLocaleString()}
													labelFormatter={label => String(label).charAt(0).toUpperCase() + String(label).slice(1)}
												/>

												<Area
													type="monotone"
													dataKey="count"
													stroke="#0088FE"
													fill="#0088FE"
													strokeWidth={2}
													dot={{ r: 4 }}
													activeDot={{ r: 6 }}
												>
													<LabelList
														dataKey="count"
														content={renderCustomLabelAlt}
														position="top"
														style={{ fontSize: '12px', fill: '#333' }}
													/>
												</Area>
											</AreaChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>

					{/* IVF enrollment Closed*/}
					<div className="col-sm-12">
						<div className="element-wrapper">
							<div className="d-flex justify-content-between align-items-center">
								<h6 className="element-header">IVF Enrollment Closed By Staff</h6>
								<TimeRangeDropdownAlt3 value={timeRanges.enrollmentClosed} onChange={handleTimeRangeChange} chartId="enrollmentClosed" />
							</div>
							<p className="chart-description mb-3 p-2 card">
								Shows how IVF enrollments were closed by different staff members within the selected time frame. Useful for monitoring staff
								activity, accountability, and workflow efficiency.
							</p>
							<div className="element-box">
								{loadingStates?.enrollmentClosed ? (
									<TableLoading />
								) : enrollmentClosed?.length > 0 ? (
									<div style={{ height: '400px' }}>
										<ResponsiveContainer>
											<BarChart data={enrollmentClosed} margin={{ top: 20, right: 40, left: 20, bottom: 40 }}>
												<CartesianGrid strokeDasharray="3 3" />
												<XAxis
													dataKey={'name'}
													angle={-45}
													textAnchor="end"
													fontSize={12}
													interval={0}
													tickFormatter={value => {
														const truncated = value.length > 10 ? value.substring(0, 10) + '...' : value;
														const capitalized = truncated
															.toLowerCase()
															.split(' ')
															.map(word => word.charAt(0).toUpperCase() + word.slice(1))
															.join(' ');
														return capitalized;
													}}
												/>
												<YAxis domain={[0, 'auto']} />

												<Tooltip content={CustomTooltipAlt} />
												<Bar dataKey="count" fill="#8884d8">
													<LabelList dataKey="count" position="top" />
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</div>
								) : (
									<div className="text-center fs-bold">No Record Found!</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default IVFOverview;
